import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useModalTelemetry, type ModalName } from '@/lib/modalTelemetry'
import s from '@/shared/components/HelpIcon.module.css'

interface Props {
  /** Which help tooltip this is, for telemetry. The title is a metric label and may be interpolated. */
  track: ModalName
  /** Tooltip heading - what the metric is. */
  title: string
  /** Tooltip body - what the metric *implies* and what to do about it. */
  children: ReactNode
  /** Optional tooltip kicker line (e.g. `// analytics`). */
  kicker?: ReactNode
  /** aria-label for the trigger; defaults to `Help: <title>`. */
  label?: string
}

/** Grace period for the pointer to cross the gap between trigger and panel. */
const CLOSE_DELAY_MS = 120

/**
 * A `?` icon that shows an interpretive explanation of an analytics metric in a hover
 * tooltip. The panel is portaled to <body> and positioned fixed from the trigger's rect:
 * `.card` clips (overflow: hidden) and the `.fu` fade-up's `forwards` fill leaves a
 * transform on the card, making it the containing block even for fixed descendants -
 * so nothing anchored inside the card can escape it.
 */
export function HelpIcon({ track, title, children, kicker, label }: Props) {
  const [pos, setPos] = useState<React.CSSProperties | null>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  const closeTimer = useRef<number | undefined>(undefined)
  const tipId = useId()

  useEffect(() => () => window.clearTimeout(closeTimer.current), [])

  const show = () => {
    window.clearTimeout(closeTimer.current)
    const r = btnRef.current?.getBoundingClientRect()
    if (!r) return
    // Right-aligned to the trigger (the icon sits at the card's right edge, so the
    // panel grows leftward). Flip above when the space below is too tight.
    const right = Math.max(window.innerWidth - r.right - 4, 8)
    const spaceBelow = window.innerHeight - r.bottom
    setPos(
      spaceBelow >= 260
        ? { top: r.bottom + 6, right, maxHeight: spaceBelow - 18 }
        : { bottom: window.innerHeight - r.top + 6, right, maxHeight: r.top - 18 },
    )
  }
  const hideSoon = () => {
    window.clearTimeout(closeTimer.current)
    closeTimer.current = window.setTimeout(() => setPos(null), CLOSE_DELAY_MS)
  }
  const hideNow = () => {
    window.clearTimeout(closeTimer.current)
    setPos(null)
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={s.trigger}
        aria-label={label ?? `Help: ${title}`}
        aria-expanded={pos !== null}
        aria-describedby={pos ? tipId : undefined}
        onMouseEnter={show}
        onMouseLeave={hideSoon}
        onFocus={show}
        onBlur={hideNow}
        onKeyDown={(e) => { if (e.key === 'Escape') hideNow() }}
        // Touch has no hover; a tap (which fires click) opens the tooltip.
        onClick={show}
      >
        ?
      </button>

      {pos !== null &&
        createPortal(
          <div
            id={tipId}
            role="tooltip"
            className={s.tip}
            style={pos}
            onMouseEnter={() => window.clearTimeout(closeTimer.current)}
            onMouseLeave={hideSoon}
          >
            <TipContent track={track} title={title} kicker={kicker}>
              {children}
            </TipContent>
          </div>,
          document.body,
        )}
    </>
  )
}

/**
 * Separate component so useModalTelemetry's mount effect runs per open: the panel is
 * mounted only while visible, so mount === open and unmount === close, exactly the
 * contract the hook was written against for <Modal>.
 */
function TipContent({ track, title, kicker, children }: {
  track: ModalName
  title: string
  kicker?: ReactNode
  children: ReactNode
}) {
  useModalTelemetry(track)
  return (
    <>
      <div className={s.header}>
        {kicker && <div className={s.kicker}>{kicker}</div>}
        <div className={s.title}>{title}</div>
      </div>
      <div className={s.body}>{children}</div>
    </>
  )
}
